import React, { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Upload, Download, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

const REQUIRED = ["company_name"];
const OPTIONAL = [
  "contact_person", "email", "phone", "country", "city",
  "assigned_bdm_email", "status", "commission_agreement", "notes",
];

const TEMPLATE = `company_name,contact_person,email,phone,country,city,assigned_bdm_email,status,commission_agreement,notes
Maple Edge Consultants,Rohit Sharma,rohit@mapleedge.in,+91-9811100000,India,Delhi,,Active,15% flat,Preferred partner for Punjab region
Sunrise Education Group,Anita Verma,anita@sunrise.pk,+92-3211122333,Pakistan,Lahore,,Active,10% flat,Focus on undergrad diplomas
Cedar Overseas,Arjun Kaur,arjun@cedaroverseas.ca,+1-6041234567,Canada,Vancouver,,Inactive,Custom per intake,Paused for Q4`;

export default function PartnersImportDialog({ open, onOpenChange, bdmUsers = [], onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [defBdm, setDefBdm] = useState("");
  const [defStatus, setDefStatus] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setRows([]); setFileName(""); setDefBdm(""); setDefStatus("");
    setResult(null); setImporting(false);
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => { reset(); onOpenChange(false); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
      complete: (res) => {
        const parsed = (res.data || []).filter(r => Object.values(r).some(v => (v || "").toString().trim() !== ""));
        setRows(parsed);
        if (!parsed.length) toast.error("No usable rows found in CSV");
        else toast.success(`Parsed ${parsed.length} rows`);
      },
      error: (err) => toast.error("CSV parse failed: " + err.message),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "partners-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const importNow = async () => {
    if (!rows.length) { toast.error("Upload a CSV first"); return; }
    const defaults = {};
    if (defBdm) defaults.assigned_bdm_id = defBdm;
    if (defStatus) defaults.status = defStatus;
    setImporting(true);
    try {
      const r = await api.post("/partners/import", { rows, defaults });
      setResult(r.data);
      if (r.data.imported > 0) toast.success(`Imported ${r.data.imported} partner${r.data.imported === 1 ? "" : "s"}`);
      if ((r.data.errors || []).length) toast.message(`${r.data.errors.length} row(s) skipped — see details`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const finish = () => { onImported?.(); close(); };

  const preview = rows.slice(0, 5);
  const previewCols = ["company_name", "contact_person", "email", "phone", "country", "city", "assigned_bdm_email", "status"];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent data-testid="partners-import-dialog" className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading-font">Bulk Import Partners from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV of partners. <span className="font-semibold text-slate-800">company_name</span> is required; everything else is optional. Duplicates (same email) are skipped. Use batch defaults below to fill empty rows.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 p-3 border border-dashed border-slate-300 rounded-lg">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <Upload className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" title={fileName}>{fileName || "Choose a .csv file"}</div>
                  <div className="text-xs text-slate-500">
                    Required: <span className="mono">{REQUIRED.join(", ")}</span> · Optional: <span className="mono">{OPTIONAL.join(", ")}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input ref={inputRef} type="file" accept=".csv" onChange={onFile} className="hidden" id="partners-csv-input" data-testid="partners-csv-input" />
                <label htmlFor="partners-csv-input" className="inline-flex items-center px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm cursor-pointer hover:bg-slate-800">
                  Choose CSV
                </label>
                <Button data-testid="partners-csv-template" variant="outline" size="sm" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-1" />Template
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Default BDM (fills empty rows)</Label>
                <Select value={defBdm} onValueChange={setDefBdm}>
                  <SelectTrigger data-testid="partners-default-bdm"><SelectValue placeholder="No default" /></SelectTrigger>
                  <SelectContent>
                    {bdmUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Default Status</Label>
                <Select value={defStatus} onValueChange={setDefStatus}>
                  <SelectTrigger data-testid="partners-default-status"><SelectValue placeholder="Active" /></SelectTrigger>
                  <SelectContent>
                    {["Active", "Inactive", "Archived"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {rows.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                  <span><strong>{rows.length}</strong> rows parsed · showing first {preview.length}</span>
                  <button onClick={() => { setRows([]); setFileName(""); if (inputRef.current) inputRef.current.value = ""; }} className="text-rose-600 hover:underline text-xs flex items-center">
                    <X className="w-3 h-3 mr-0.5" />Clear
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-500 uppercase">
                      <tr>{previewCols.map((h) => (<th key={h} className="px-2 py-1 text-left whitespace-nowrap">{h}</th>))}</tr>
                    </thead>
                    <tbody>
                      {preview.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {previewCols.map((h) => (
                            <td key={h} className="px-2 py-1 whitespace-nowrap">
                              {r[h] || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <div className="font-semibold text-emerald-900">Imported {result.imported} partner{result.imported === 1 ? "" : "s"}</div>
                <div className="text-xs text-emerald-800">{(result.errors || []).length} error{(result.errors || []).length === 1 ? "" : "s"}</div>
              </div>
            </div>
            {(result.errors || []).length > 0 && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-amber-900 font-medium text-sm mb-2">
                  <AlertTriangle className="w-4 h-4" />Rows that could not be imported
                </div>
                <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((e, i) => (
                    <div key={i}>Row {e.row}: <span className="text-amber-900">{e.reason}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button data-testid="partners-import-confirm" onClick={importNow} disabled={!rows.length || importing} className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50">
                {importing ? "Importing…" : `Import ${rows.length || ""} partners`}
              </Button>
            </>
          ) : (
            <Button data-testid="partners-import-done" onClick={finish} className="bg-slate-900 hover:bg-slate-800 text-white">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
